import { Suspense, lazy } from "react";

import { UserLevel } from "pages/[tenant]/g/components/Feed/components/UserLevel";

import { ComponentErrorBoundary } from "shared/components/atoms/ComponentErrorBoundary";
import { GamificationModal } from "shared/components/molecules/Gamification/GamificationModal";
import { INotification } from "shared/components/organisms/AppNotification/@types";
import { ILevelUpMetadata } from "shared/components/organisms/AppNotification/@types/levelUpTypes";
import { LevelUpIcon } from "shared/components/organisms/AppNotification/assets/icons/LevelUpIcon";
import { Notification } from "shared/components/organisms/AppNotification/components/molecules/Notification";
import { formatTimeAgo } from "shared/components/organisms/AppNotification/utils/formatTimeAgo";
import { useTopbarContext } from "shared/providers/Layout/hooks/useTopbarContext";
import componentLoader from "shared/utils/componentLoader";
import { useGlobalContext } from "shared/utils/hooks/useGlobalContext";

import { LevelUpNotificationContainer } from "./styles";

const Confetti = lazy(() =>
  componentLoader(
    () =>
      import(
        /* webpackChunkFilename: 'Confetti', filename: 'Confetti' */
        "react-confetti"
      ),
  ),
);

interface ILevelUpNotificationProps {
  notification: INotification<ILevelUpMetadata>;
}

export const LevelUpNotification = ({
  notification,
}: ILevelUpNotificationProps) => {
  const {
    state: { tenancy },
  } = useGlobalContext();
  const pointsAbbreviation =
    tenancy?.settings?.gamification?.pointsNameAbbreviation;

  const circularProgressRate =
    (notification?.metadata?.user?.total_points /
      notification?.metadata?.next_level?.minimum_points) *
    100;

  const { notificationsIsOpen } = useTopbarContext();
  return (
    <LevelUpNotificationContainer>
      {notificationsIsOpen && (
        <ComponentErrorBoundary>
          <Suspense fallback={<></>}>
            <Confetti
              width={300}
              height={300}
              recycle={notificationsIsOpen}
              gravity={0.01}
            />
          </Suspense>
        </ComponentErrorBoundary>
      )}

      <Notification.Container wasReaded={!!notification?.read_at}>
        <Notification.IllustrationContainer>
          <LevelUpIcon />
        </Notification.IllustrationContainer>

        <div>
          <Notification.Title.Container>
            <Notification.Title.Highlight>
              Você subiu de nível!
            </Notification.Title.Highlight>
          </Notification.Title.Container>

          <Notification.Content.Text>
            Parabéns! Você conquistou mais <b>{pointsAbbreviation}</b> e
            alcançou o nível <b>{notification?.metadata?.new_level?.title}</b>.
          </Notification.Content.Text>

          <Notification.Footer.Container>
            <Notification.Footer.Text>
              {formatTimeAgo(notification?.created_at)}
            </Notification.Footer.Text>
          </Notification.Footer.Container>
        </div>

        <div className="level-container">
          <GamificationModal.UserPhoto
            fullName={notification?.metadata?.user?.display_name}
            photoSrc={notification?.metadata?.user?.photo}
            circularProgressRate={circularProgressRate}
            withStars
          />
          <UserLevel
            level={{
              color: notification?.metadata?.new_level?.color,
              seal_image: notification?.metadata?.new_level?.seal_image,
              title: notification?.metadata?.new_level?.title,
            }}
            size={32}
          />
        </div>
      </Notification.Container>
    </LevelUpNotificationContainer>
  );
};
